import type {
  ExecutionContext,
  ProjectDetail,
  ProjectInvoiceRef,
  PhaseLink,
  PhaseMove,
  ProjectFilter,
  ProjectLot,
  ProjectRepository,
  ProjectStatus,
  ProjectPhase,
  ProjectSummary,
  SaveLotCommand,
  SavePhaseCommand,
  SaveProjectCommand,
} from "@dker/core";
import { DomainError, computeProjectHealth, invoicedHtByContract, orderPhases } from "@dker/core";
import type { DbClientFactory } from "../ports.js";
import {
  INVOICE_COLUMNS as SUBCONTRACT_INVOICE_COLUMNS,
  PAYMENT_COLUMNS as SUBCONTRACT_PAYMENT_COLUMNS,
  SUBCONTRACT_COLUMNS,
  toSubcontract,
  toSubcontractInvoice,
  toSubcontractPayment,
} from "./subcontracting.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const num = (v: unknown) => Number(v ?? 0);

const PHASE_COLUMNS = "id, name, sort_order, progress_percent, status, planned_start, planned_end, lot_id";

function toPhase(row: Row): ProjectPhase {
  return {
    id: row.id,
    name: row.name,
    sortOrder: num(row.sort_order),
    progressPercent: num(row.progress_percent),
    status: row.status,
    plannedStart: row.planned_start ?? null,
    plannedEnd: row.planned_end ?? null,
    lotId: row.lot_id ?? null,
  };
}

const LOT_COLUMNS = "id, project_id, name, sort_order, notes";

function toLot(row: Row): ProjectLot {
  return {
    id: row.id,
    name: row.name,
    sortOrder: num(row.sort_order),
    notes: row.notes ?? null,
  };
}

function toLink(row: Row): PhaseLink {
  return { phaseId: row.phase_id, dependsOnId: row.depends_on_id, lagDays: num(row.lag_days) };
}

const SUMMARY_COLUMNS = `
  id, name, status, status_reason, status_changed_at, site_address, site_postal_code, site_city,
  site_gps_lat, site_gps_lng, planned_start_date, planned_end_date,
  budget_ht, spent_ht,
  client:clients(id, first_name, last_name, company_name, email)
`;

/** Les colonnes dont dépend le chiffre d'affaires d'un chantier. */
const INVOICE_COLUMNS = "id, project_id, quote_id, type, status, total_ht";

function toInvoiceRef(row: Row): ProjectInvoiceRef {
  return {
    id: row.id,
    quoteId: row.quote_id ?? null,
    type: row.type,
    status: row.status,
    totalHt: num(row.total_ht),
  };
}

function toSummary(
  row: Row,
  phases: Row[] = [],
  invoices: Row[] = [],
  lotRows: Row[] = [],
  subcontractRows: Row[] = [],
  /** Le HT facturé par chaque marché : la marge le compare à l'avancement. */
  invoicedByContract: ReadonlyMap<string, number> = new Map(),
): ProjectSummary {
  const lots = lotRows.map(toLot).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "fr"));
  const client = Array.isArray(row.client) ? row.client[0] : row.client;
  const budgetHt = row.budget_ht == null ? null : num(row.budget_ht);
  const spentHt = num(row.spent_ht);

  return {
    id: row.id,
    name: row.name,
    status: row.status as ProjectStatus,
    statusReason: row.status_reason ?? null,
    statusChangedAt: row.status_changed_at ?? null,
    client: client
      ? {
          id: client.id,
          firstName: client.first_name ?? null,
          lastName: client.last_name ?? null,
          companyName: client.company_name ?? null,
          email: client.email ?? null,
        }
      : null,
    siteAddress: row.site_address ?? null,
    sitePostalCode: row.site_postal_code ?? null,
    siteCity: row.site_city ?? null,
    // Les deux coordonnées ou aucune : une latitude seule n'ouvre pas un GPS.
    siteGps:
      row.site_gps_lat != null && row.site_gps_lng != null
        ? { lat: num(row.site_gps_lat), lng: num(row.site_gps_lng) }
        : null,
    plannedStartDate: row.planned_start_date ?? null,
    plannedEndDate: row.planned_end_date ?? null,
    budgetHt,
    spentHt,
    health: computeProjectHealth(
      budgetHt,
      spentHt,
      phases.map((p) => ({ id: p.id, lotId: p.lot_id ?? null, progressPercent: num(p.progress_percent) })),
      invoices.map(toInvoiceRef),
      subcontractRows.map((c) => ({
        amountHt: num(c.amount_ht),
        status: c.status,
        lotId: c.lot_id ?? null,
        phaseId: c.phase_id ?? null,
        invoicedHt: invoicedByContract.get(c.id) ?? 0,
      })),
    ),
    phases: orderPhases(phases.map(toPhase), lots),
    lots,
  };
}

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabaseProjectRepository(
  clientFor: DbClientFactory,
): ProjectRepository {
  return {
    async list(ctx: ExecutionContext, filter?: ProjectFilter): Promise<ProjectSummary[]> {
      const db = clientFor(ctx);

      let query = db
        .from("projects")
        .select(SUMMARY_COLUMNS)
        .is("deleted_at", null)
        .order("planned_start_date", { ascending: false });

      if (filter?.status?.length) query = query.in("status", filter.status);
      if (filter?.clientId) query = query.eq("client_id", filter.clientId);

      const { data, error } = await query;
      if (error) fail(error);

      const projects = data ?? [];
      if (projects.length === 0) return [];

      // Les phases portent l'avancement réel, et leurs dates la surveillance
      // des délais. Une seule requête pour tous les chantiers plutôt qu'une par
      // ligne : sinon la liste fait N+1 appels.
      const { data: phases } = await db
        .from("project_phases")
        .select(`project_id, ${PHASE_COLUMNS}`)
        .in(
          "project_id",
          projects.map((p: Row) => p.id),
        );

      // Le facturé porte la marge. Groupé lui aussi : une requête par
      // chantier ferait autant d'allers-retours que la liste a de lignes.
      const { data: invoices } = await db
        .from("invoices")
        .select(INVOICE_COLUMNS)
        .in(
          "project_id",
          projects.map((p: Row) => p.id),
        );

      const grouper = (rows: Row[] | null | undefined) => {
        const parChantier = new Map<string, Row[]>();
        for (const row of rows ?? []) {
          const liste = parChantier.get(row.project_id) ?? [];
          liste.push(row);
          parChantier.set(row.project_id, liste);
        }
        return parChantier;
      };

      // Les lots ordonnent et nomment les phases — « Plomberie · Évacuations »
      // dans la surveillance des délais. Groupés, pour la même raison.
      const { data: lots } = await db
        .from("project_lots")
        .select(LOT_COLUMNS)
        .in(
          "project_id",
          projects.map((p: Row) => p.id),
        );

      // La sous-traitance entre dans la marge. Le terrain n'en lit rien : la
      // base ferme la table, et ses marges n'en tiennent pas compte — il ne
      // les voit pas non plus.
      const { data: marches } = await db
        .from("project_subcontracts")
        .select("id, project_id, lot_id, phase_id, amount_ht, status")
        .in(
          "project_id",
          projects.map((p: Row) => p.id),
        );
      const { data: facturesSt } = await db
        .from("subcontract_invoices")
        .select("subcontract_id, amount_ht")
        .in(
          "project_id",
          projects.map((p: Row) => p.id),
        );
      const factureParMarche = invoicedHtByContract(
        (facturesSt ?? []).map((r: Row) => ({ subcontractId: r.subcontract_id, amountHt: num(r.amount_ht) })),
      );

      const phasesParChantier = grouper(phases);
      const facturesParChantier = grouper(invoices);
      const lotsParChantier = grouper(lots);
      const marchesParChantier = grouper(marches);

      return projects.map((p: Row) =>
        toSummary(
          p,
          phasesParChantier.get(p.id) ?? [],
          facturesParChantier.get(p.id) ?? [],
          lotsParChantier.get(p.id) ?? [],
          marchesParChantier.get(p.id) ?? [],
          factureParMarche,
        ),
      );
    },

    async findById(ctx: ExecutionContext, projectId: string): Promise<ProjectDetail | null> {
      const db = clientFor(ctx);

      const [project, phases, orders, tasks, invoices, lots, links, subcontracts, stInvoices, stPayments] =
        await Promise.all([
        db
          .from("projects")
          .select(`${SUMMARY_COLUMNS}, description, notes`)
          .eq("id", projectId)
          .is("deleted_at", null)
          .maybeSingle(),
        db
          .from("project_phases")
          .select(PHASE_COLUMNS)
          .eq("project_id", projectId)
          .order("sort_order"),
        db
          .from("material_orders")
          .select("id, status, total_ht, needed_by")
          .eq("project_id", projectId)
          .order("created_at", { ascending: false }),
        db
          .from("tasks")
          .select("id, title, status, priority, due_date")
          .eq("project_id", projectId)
          .order("due_date"),
        db.from("invoices").select(INVOICE_COLUMNS).eq("project_id", projectId),
        db.from("project_lots").select(LOT_COLUMNS).eq("project_id", projectId),
        db
          .from("project_phase_links")
          .select("phase_id, depends_on_id, lag_days")
          .eq("project_id", projectId),
        db
          .from("project_subcontracts")
          .select(SUBCONTRACT_COLUMNS)
          .eq("project_id", projectId)
          .order("created_at"),
        db
          .from("subcontract_invoices")
          .select(SUBCONTRACT_INVOICE_COLUMNS)
          .eq("project_id", projectId)
          .order("invoice_date"),
        db
          .from("subcontract_payments")
          .select(SUBCONTRACT_PAYMENT_COLUMNS)
          .eq("project_id", projectId)
          .order("paid_on"),
      ]);

      if (project.error) fail(project.error);
      if (!project.data) return null;

      const phaseRows = phases.data ?? [];

      const subcontractInvoices = (stInvoices.data ?? []).map(toSubcontractInvoice);
      const summary = toSummary(
        project.data,
        phaseRows,
        invoices.data ?? [],
        lots.data ?? [],
        subcontracts.data ?? [],
        invoicedHtByContract(subcontractInvoices),
      );
      return {
        ...summary,
        lots: summary.lots ?? [],
        links: (links.data ?? []).map(toLink),
        subcontracts: (subcontracts.data ?? []).map(toSubcontract),
        subcontractInvoices,
        subcontractPayments: (stPayments.data ?? []).map(toSubcontractPayment),
        description: project.data.description ?? null,
        notes: project.data.notes ?? null,
        orders: (orders.data ?? []).map((o: Row) => ({
          id: o.id,
          status: o.status,
          totalHt: num(o.total_ht),
          neededBy: o.needed_by ?? null,
        })),
        tasks: (tasks.data ?? []).map((t: Row) => ({
          id: t.id,
          title: t.title,
          status: t.status,
          priority: t.priority,
          dueDate: t.due_date ?? null,
        })),
      };
    },

    async save(ctx: ExecutionContext, command: SaveProjectCommand): Promise<ProjectSummary> {
      const db = clientFor(ctx);
      const columns: Row = {
        client_id: command.clientId,
        name: command.name,
        description: command.description,
        site_address: command.siteAddress,
        site_postal_code: command.sitePostalCode,
        site_city: command.siteCity,
        planned_start_date: command.plannedStartDate,
        planned_end_date: command.plannedEndDate,
        budget_ht: command.budgetHt,
        quote_id: command.quoteId,
      };

      // En mise à jour, `quote_id` n'est pas touché : **un chantier ne change
      // pas de devis.** Le lien naît avec lui, par `accept_quote`, et c'est
      // par lui que ses factures le retrouvent — un écran qui enregistrerait
      // le chantier sans reporter ce champ le couperait de son marché, et donc
      // de son chiffre d'affaires. Même précaution que pour `project_id` sur
      // une phase, juste en dessous.
      const { quote_id: _devis, ...miseAJour } = columns;

      const { data, error } = command.projectId
        ? await db
            .from("projects")
            .update(miseAJour)
            .eq("id", command.projectId)
            .select(SUMMARY_COLUMNS)
            .maybeSingle()
        : await db.from("projects").insert(columns).select(SUMMARY_COLUMNS).single();

      if (error) fail(error);
      if (!data) {
        throw new DomainError("not_found", "Ce chantier n'existe pas.", {
          projectId: command.projectId,
        });
      }
      return toSummary(data);
    },

    async setStatus(ctx, projectId, status, reason) {
      const db = clientFor(ctx);
      // `status_changed_at` n'est pas écrit ici : la base le pose quand le
      // statut change vraiment, et elle efface le motif hors pause ou arrêt.
      const { data, error } = await db
        .from("projects")
        .update({ status, status_reason: reason })
        .eq("id", projectId)
        .select(SUMMARY_COLUMNS)
        .maybeSingle();
      if (error) fail(error);
      if (!data) {
        throw new DomainError("not_found", "Ce chantier n'existe pas.", { projectId });
      }
      return toSummary(data);
    },

    /**
     * Les phases : la porte d'écriture qui manquait depuis la migration
     * d'origine. La table était lue par la fiche chantier, le tableau de bord
     * et le téléphone de l'ouvrier, et rien ne permettait d'en poser une.
     */
    async savePhase(ctx: ExecutionContext, command: SavePhaseCommand) {
      const db = clientFor(ctx);
      const columns = {
        project_id: command.projectId,
        name: command.name,
        sort_order: command.sortOrder,
        progress_percent: command.progressPercent,
        status: command.status,
        planned_start: command.plannedStart,
        planned_end: command.plannedEnd,
        // Absent : on n'y touche pas. Un appel qui ne parle que d'avancement
        // ne sort pas la phase de son lot.
        ...(command.lotId !== undefined ? { lot_id: command.lotId } : {}),
      };

      // `tenant_id` n'est pas fourni : la colonne le pose seule par défaut, et
      // l'écrire à la main est la ligne qu'on n'a pas envie d'écrire dans un
      // socle multi-tenant. En mise à jour on ne touche pas au chantier — une
      // phase ne déménage pas d'un ouvrage à l'autre.
      const { project_id: _projet, ...miseAJour } = columns;

      const { data, error } = command.phaseId
        ? await db
            .from("project_phases")
            .update(miseAJour)
            .eq("id", command.phaseId)
            .select(PHASE_COLUMNS)
            .maybeSingle()
        : await db.from("project_phases").insert(columns).select(PHASE_COLUMNS).single();

      if (error) fail(error);
      if (!data) {
        throw new DomainError("not_found", "Cette phase n'existe pas.", {
          phaseId: command.phaseId,
        });
      }
      return toPhase(data as Row);
    },

    async removePhase(ctx: ExecutionContext, phaseId: string) {
      const { error } = await clientFor(ctx)
        .from("project_phases")
        .delete()
        .eq("id", phaseId);
      if (error) fail(error);
    },

    async saveLot(ctx: ExecutionContext, command: SaveLotCommand) {
      const db = clientFor(ctx);
      const columns = { name: command.name, sort_order: command.sortOrder, notes: command.notes };
      const { data, error } = command.lotId
        ? await db.from("project_lots").update(columns).eq("id", command.lotId).select(LOT_COLUMNS).maybeSingle()
        : await db
            .from("project_lots")
            .insert({ ...columns, project_id: command.projectId })
            .select(LOT_COLUMNS)
            .single();

      if (error?.code === "23505") {
        throw new DomainError("conflict", `Ce chantier a déjà un lot « ${command.name} ».`, {
          name: command.name,
        });
      }
      if (error) fail(error);
      if (!data) {
        throw new DomainError("not_found", "Ce lot n'existe pas.", { lotId: command.lotId });
      }
      return toLot(data as Row);
    },

    async removeLot(ctx: ExecutionContext, lotId: string) {
      const { error } = await clientFor(ctx).from("project_lots").delete().eq("id", lotId);
      if (error) fail(error);
    },

    async setPredecessors(ctx, phaseId, predecessors) {
      // Une fonction en base plutôt qu'un effacement suivi d'insertions : une
      // boucle refusée au troisième lien ne doit pas laisser les deux premiers
      // posés, ni les anciens effacés.
      const { error } = await clientFor(ctx).rpc("set_phase_predecessors", {
        p_phase_id: phaseId,
        p_links: predecessors.map((p) => ({ dependsOnId: p.dependsOnId, lagDays: p.lagDays })),
      });
      if (error?.message?.includes("boucle")) {
        throw new DomainError(
          "conflict",
          "Cet enchaînement ferait une boucle : une des phases attendues attend déjà celle-ci.",
          { phaseId },
        );
      }
      if (error) fail(error);
    },

    async shiftPhases(ctx: ExecutionContext, projectId: string, moves: readonly PhaseMove[]) {
      const { data, error } = await clientFor(ctx).rpc("shift_project_phases", {
        p_project_id: projectId,
        p_moves: moves,
      });
      if (error) fail(error);
      return num(data);
    },
  };
}
